import {
    isAfterDesignItem,
    overlayFileTypeForAfter,
    restoredFileTypeForAfter,
    wireframeFileTypeForAfter,
    type HistoryItem,
} from "../../../types/smileDesign/historyItem";

const BOX_UPLOAD_URL = "https://upload.box.com/api/2.0/files/content";
const boxFileVersionUploadUrl = (fileId: string) =>
    `https://upload.box.com/api/2.0/files/${fileId}/content`;

type BoxFolderEntry = {
    type: string;
    id: string;
    name: string;
};

type BoxUploadError = Error & { status?: number; body?: string };

const parseBoxConflictFileId = (errorBody: unknown): string | null => {
    const conflicts = (errorBody as { context_info?: { conflicts?: unknown } })
        ?.context_info?.conflicts;
    if (!conflicts) return null;

    const items = Array.isArray(conflicts) ? conflicts : [conflicts];
    const existing = items.find(
        (item: { type?: string; id?: string }) => item?.type === "file" && item?.id
    );

    return existing?.id ? String(existing.id) : null;
};

const listFolderEntries = async (
    folderId: string | number,
    accessToken: string
): Promise<BoxFolderEntry[]> => {
    const entries: BoxFolderEntry[] = [];
    let offset = 0;
    const limit = 1000;

    while (true) {
        const response = await fetch(
            `https://api.box.com/2.0/folders/${folderId}/items?limit=${limit}&offset=${offset}&fields=id,name,type`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );

        if (!response.ok) {
            throw new Error(await response.text());
        }

        const data = await response.json();
        const page = (data.entries ?? []) as BoxFolderEntry[];
        entries.push(...page);

        const total = data.total_count ?? entries.length;
        if (page.length === 0 || entries.length >= total) break;
        offset += limit;
    }

    return entries;
};

const findFileIdByName = async (
    folderId: string | number,
    fileName: string,
    accessToken: string
): Promise<string | null> => {
    const entries = await listFolderEntries(folderId, accessToken);
    const exact = entries.find(
        (entry) => entry.type === "file" && entry.name === fileName
    );
    if (exact?.id) return String(exact.id);

    const lower = fileName.toLowerCase();
    const caseInsensitive = entries.find(
        (entry) => entry.type === "file" && entry.name.toLowerCase() === lower
    );
    return caseInsensitive?.id ? String(caseInsensitive.id) : null;
};

const postFileToBox = async (
    url: string,
    formData: FormData,
    accessToken: string
) => {
    const response = await fetch(url, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
    });

    if (!response.ok) {
        const text = await response.text();
        const error: BoxUploadError = new Error(text);
        error.status = response.status;
        error.body = text;
        throw error;
    }

    const data = await response.json();
    return data.entries[0];
};

const uploadNewFileVersion = async (
    file: File,
    fileId: string,
    accessToken: string
) => {
    const formData = new FormData();
    formData.append(
        "attributes",
        JSON.stringify({
            name: file.name,
        })
    );
    formData.append("file", file);

    return postFileToBox(
        boxFileVersionUploadUrl(fileId),
        formData,
        accessToken
    );
};

const resolveConflictFileId = async (
    error: unknown,
    file: File,
    parentFolderId: string | number,
    accessToken: string
): Promise<string | null> => {
    const uploadError = error as BoxUploadError;
    const body = uploadError.body ?? uploadError.message ?? "";
    const isConflict =
        uploadError.status === 409 || body.includes("item_name_in_use");

    if (!isConflict) return null;

    try {
        const parsed = JSON.parse(body);
        const conflictId = parseBoxConflictFileId(parsed);
        if (conflictId) return conflictId;
    } catch {
        // Box error bodies are not always readable from the browser; fall back to listing.
    }

    return findFileIdByName(parentFolderId, file.name, accessToken);
};

export const uploadFileToBox = async (
    file: File,
    parentFolderId: number | string,
    accessToken: string
) => {
    const existingId = await findFileIdByName(
        parentFolderId,
        file.name,
        accessToken
    );
    if (existingId) {
        return uploadNewFileVersion(file, existingId, accessToken);
    }

    const formData = new FormData();
    formData.append(
        "attributes",
        JSON.stringify({
            name: file.name,
            parent: {
                id: String(parentFolderId),
            },
        })
    );
    formData.append("file", file);

    try {
        return await postFileToBox(BOX_UPLOAD_URL, formData, accessToken);
    } catch (error) {
        const conflictId = await resolveConflictFileId(
            error,
            file,
            parentFolderId,
            accessToken
        );
        if (conflictId) {
            return uploadNewFileVersion(file, conflictId, accessToken);
        }

        console.error((error as BoxUploadError).body ?? error);
        throw error instanceof Error ? error : new Error(String(error));
    }
};








export const uploadImageToBox = async (
    imageUrl: string,
    fileName: string,
    parentFolderId: number | string,
    accessToken: string
) => {
    const imageResponse = await fetch(imageUrl);

    if (!imageResponse.ok) {
        throw new Error(
            `Failed to read image "${fileName}" (${imageResponse.status})`
        );
    }

    const blob = await imageResponse.blob();
    if (!blob.size) {
        throw new Error(`Empty image "${fileName}"`);
    }

    const file = new File(
        [blob],
        fileName,
        {
            type: blob.type || "image/png",
        }
    );

    return uploadFileToBox(
        file,
        parentFolderId,
        accessToken
    );
};


export const createBoxFolder = async (
    folderName: string,
    parentFolderId: number | string,
    accessToken: string
) => {
    const response = await fetch(
        "https://api.box.com/2.0/folders",
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                name: folderName,
                parent: {
                    id: String(parentFolderId),
                },
            }),
        }
    );

    if (response.ok) {
        return response.json();
    }

    if (response.status === 409) {
        const entries = await listFolderEntries(parentFolderId, accessToken);
        const existing = entries.find(
            (item) => item.type === "folder" && item.name === folderName
        );
        if (existing) return existing;
    }

    throw new Error("Failed to create folder");
};

export const getOrCreateGenerationsFolder = async (
    parentFolderId: number | string,
    accessToken: string
) => {
    const entries = await listFolderEntries(parentFolderId, accessToken);
    const existingFolder = entries.find(
        (item) => item.type === "folder" && item.name === "Generations"
    );

    if (existingFolder) {
        return existingFolder;
    }

    return createBoxFolder(
        "Generations",
        parentFolderId,
        accessToken
    );
};

export const uploadSmileCaseFiles = async ({
    file,
    history,
    patientName,
    caseBoxId,
    accessToken,
    uploadInput,
    /** When true, skip standalone after images — still upload unsaved afters that have attachments */
    wireframesOnly = false,
}: {
    file: File;
    patientName: string;
    history: HistoryItem[];
    caseBoxId: number;
    accessToken: string;
    uploadInput: boolean;
    wireframesOnly?: boolean;
}) => {
    const payload: { box_id: string; name: string; type: string }[] = [];

    if (uploadInput && !wireframesOnly) {
        const extension = file.name.split(".").pop();

        const renamedInputFile = new File(
            [file],
            `${patientName}-input.${extension}`,
            {
                type: file.type,
            }
        );

        const uploadedInput = await uploadFileToBox(
            renamedInputFile,
            caseBoxId,
            accessToken
        );

        payload.push({
            box_id: uploadedInput.id,
            name: uploadedInput.name,
            type: "Input",
        });
    }

    const generationsFolder =
        await getOrCreateGenerationsFolder(
            caseBoxId,
            accessToken
        );

    const afterUploads = await Promise.all(
        history
            .filter((item) => {
                if (!isAfterDesignItem(item) || item.saved) return false;
                if (!wireframesOnly) return true;
                // Stage 2 save still persists the parent after when it has
                // unsaved attachments, otherwise restored images cannot rematch
                // on reload.
                return (
                    (item.wireframe && !item.wireframe.saved) ||
                    (item.overlay && !item.overlay.saved) ||
                    (item.restored && !item.restored.saved)
                );
            })
            .map(async (item) => {
                const uploaded = await uploadImageToBox(
                    item.image,
                    item.imageName,
                    generationsFolder.id,
                    accessToken
                );

                return {
                    box_id: uploaded.id,
                    name: uploaded.name,
                    type: item.smileType,
                };
            })
    );

    payload.push(...afterUploads);

    const wireframeUploads = await Promise.all(
        history
            .filter(
                (item) =>
                    isAfterDesignItem(item) &&
                    item.wireframe &&
                    !item.wireframe.saved
            )
            .map(async (item) => {
                const wf = item.wireframe!;
                const uploaded = await uploadImageToBox(
                    wf.image,
                    wf.imageName,
                    generationsFolder.id,
                    accessToken
                );

                return {
                    box_id: uploaded.id,
                    name: uploaded.name,
                    type: wireframeFileTypeForAfter(item.smileType),
                };
            })
    );

    payload.push(...wireframeUploads);

    const overlayUploads = await Promise.all(
        history
            .filter(
                (item) =>
                    isAfterDesignItem(item) &&
                    item.overlay &&
                    !item.overlay.saved
            )
            .map(async (item) => {
                const ov = item.overlay!;
                const uploaded = await uploadImageToBox(
                    ov.image,
                    ov.imageName,
                    generationsFolder.id,
                    accessToken
                );

                return {
                    box_id: uploaded.id,
                    name: uploaded.name,
                    type: overlayFileTypeForAfter(item.smileType),
                };
            })
    );

    payload.push(...overlayUploads);

    const restoredUploads = await Promise.all(
        history
            .filter(
                (item) =>
                    isAfterDesignItem(item) &&
                    item.restored &&
                    !item.restored.saved
            )
            .map(async (item) => {
                const rs = item.restored!;
                const uploaded = await uploadImageToBox(
                    rs.image,
                    rs.imageName,
                    generationsFolder.id,
                    accessToken
                );

                return {
                    box_id: uploaded.id,
                    name: uploaded.name,
                    type: restoredFileTypeForAfter(item.smileType),
                };
            })
    );

    payload.push(...restoredUploads);

    return payload;
};